import './styles.css';

import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from 'uuid';

import { FaDownload, FaPlus, FaRegTrashAlt } from "react-icons/fa";
import { IoMdCheckboxOutline } from "react-icons/io";
import { CgCloseR } from "react-icons/cg";
import { LuSparkles } from "react-icons/lu";

import Loader from "../../Atoms/Loader";
import Container from "../../Atoms/Container";
import ButtonMain from "../../Atoms/ButtonMain";
import ButtonSecondary from "../../Atoms/ButtonSecondary";
import AnimatedBrainLoader from "../../Atoms/AnimatedBrainLoader";

import IdeaCard from "../BrainstormIdeas/Molecules/IdeaCard";
import EditIdeaModal from "../BrainstormIdeas/Molecules/EditIdeaModal";
import NewIdeaModal from "../BrainstormIdeas/Molecules/NewIdeaModal";
import KnowledgeBaseModal from "./Molecules/KnowledgeBaseModal";

import { 
    getBrainstormById, 
    generateIdeasByGuidingQuestion, 
    renderIdeasPDF, 
    createAssetKnowledgeBase,
    updateBrainstorm
} from "../../../services/Brainstorm/BrainstormService";

const MAX_IDEAS_PER_QUESTION = 38;


export default function BrainstormIdeasEditTemplate() {
    const { projectId, outputId } = useParams();
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();
    const KEYCLOACK_TOKEN = keycloak.token;

    const [currentBrainstorm, setCurrentBrainstorm] = useState(null);
    const [selectedGuidingQuestion, setSelectedGuidingQuestion] = useState(null);
    const [guidingQuestions, setGuidingQuestions] = useState([]);
    const [ideas, setIdeas] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingIdea, setEditingIdea] = useState(null);
    const [showNewIdeaModal, setShowNewIdeaModal] = useState(false);
    const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState(false);
    const [isGeneratingMoreIdeas, setIsGeneratingMoreIdeas] = useState(false);
    const [isDownloadDisabled, setIsDownloadDisabled] = useState(true);
    const [loadingPDF, setLoadingPDF] = useState(false);
    const [loadingKnowledgeBase, setLoadingKnowledgeBase] = useState(false);

    useEffect(() => {
        const loadBrainstormData = async () => {
            if (!outputId || !KEYCLOACK_TOKEN) return;

            setIsLoadingData(true);
            try {
                const brainstormData = await getBrainstormById(outputId, KEYCLOACK_TOKEN);
                
                if (!brainstormData) {
                    Swal.fire({
                        title: 'Erro',
                        text: 'Brainstorm não encontrado.',
                        timer: 2000,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
                    navigate(`/project/${projectId}`);
                    return;
                }

                setCurrentBrainstorm(brainstormData.data);

                const selectedChallenge = brainstormData.data.challenges?.find(challenge => challenge.selected === true);

                if (!selectedChallenge) {
                    console.warn('No selected challenge found');
                    setIsLoadingData(false);
                    return;
                }

                const guidingQuestionsArr = Object.values(brainstormData.data.guiding_questions);
                const challengeGuidingQuestions = guidingQuestionsArr.filter(
                    question => question.challenge_id === selectedChallenge.id
                );
                const selectedGuidingQuestions = challengeGuidingQuestions.filter(
                    question => question.selected === true
                );
                setGuidingQuestions(selectedGuidingQuestions);

                if (selectedGuidingQuestions.length === 0) {
                    console.warn('No guiding questions found for selected challenge');
                    setIsLoadingData(false);
                    return;
                }

                const firstQuestion = selectedGuidingQuestions[0];
                setSelectedGuidingQuestion(firstQuestion.id);
                setIdeas(firstQuestion.ideas || []);
            } catch (error) {
                console.error('Error loading brainstorm data:', error);
                Swal.fire({
                    title: 'Ocorreu um erro',
                    text: 'Erro ao carregar dados do brainstorm. Tente novamente mais tarde',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                navigate(`/project/${projectId}`);
            } finally {
                setIsLoadingData(false);
            }
        };

        loadBrainstormData();
    }, [outputId, KEYCLOACK_TOKEN, projectId, navigate]);

    useEffect(() => {
        if (selectedGuidingQuestion && currentBrainstorm) {
            const guidingQuestionsArr = Object.values(currentBrainstorm.guiding_questions);
            const question = guidingQuestionsArr.find(q => q.id === selectedGuidingQuestion);
            setIdeas(question?.ideas || []);
        }
    }, [selectedGuidingQuestion, currentBrainstorm]);

    useEffect(() => {
        if (!currentBrainstorm) {
            setIsDownloadDisabled(true);
            return;
        }

        const guidingQuestionsArr = Object.values(currentBrainstorm.guiding_questions);
        const hasAnySelectedIdeas = guidingQuestionsArr?.some(
            question => question.ideas?.some(idea => idea.selected === true)
        ) || false;
        
        setIsDownloadDisabled(!hasAnySelectedIdeas);
    }, [currentBrainstorm]);
    
    const getIdeasByGuidingQuestionId = useCallback((questionId) => {
        if (!currentBrainstorm) return [];
        const guidingQuestionsArr = Object.values(currentBrainstorm.guiding_questions);
        const question = guidingQuestionsArr.find(q => q.id === questionId);
        return question?.ideas || [];
    }, [currentBrainstorm]);

    // eslint-disable-next-line no-unused-vars
    const updateBrainstormState = useCallback((updates) => {
        setCurrentBrainstorm(prev => prev ? { ...prev, ...updates, updatedAt: new Date().toISOString() } : null);
    }, []);

    // eslint-disable-next-line no-unused-vars
    const updateGuidingQuestionInState = useCallback((questionId, updates) => {
        setCurrentBrainstorm(prev => {
            if (!prev) return null;
            const updatedGuidingQuestions = { ...prev.guiding_questions };
            Object.keys(updatedGuidingQuestions).forEach(key => {
                if (updatedGuidingQuestions[key].id === questionId) {
                    updatedGuidingQuestions[key] = { ...updatedGuidingQuestions[key], ...updates };
                }
            });
            return {
                ...prev,
                guiding_questions: updatedGuidingQuestions,
                updatedAt: new Date().toISOString()
            };
        });
    }, []);

    const updateIdeaInState = useCallback((questionId, ideaId, updates) => {
        setCurrentBrainstorm(prev => {
            if (!prev) return null;
            const updatedGuidingQuestions = { ...prev.guiding_questions };
            Object.keys(updatedGuidingQuestions).forEach(key => {
                if (updatedGuidingQuestions[key].id === questionId) {
                    const question = updatedGuidingQuestions[key];
                    updatedGuidingQuestions[key] = {
                        ...question,
                        ideas: question.ideas?.map(idea =>
                            idea.id === ideaId ? { ...idea, ...updates } : idea
                        ) || []
                    };
                }
            });
            return {
                ...prev,
                guiding_questions: updatedGuidingQuestions,
                updatedAt: new Date().toISOString()
            };
        });
    }, []);

    const updateMultipleIdeasInState = useCallback((questionId, updates) => {
        setCurrentBrainstorm(prev => {
            if (!prev) return null;
            const updatedGuidingQuestions = { ...prev.guiding_questions };
            Object.keys(updatedGuidingQuestions).forEach(key => {
                if (updatedGuidingQuestions[key].id === questionId) {
                    const question = updatedGuidingQuestions[key];
                    updatedGuidingQuestions[key] = {
                        ...question,
                        ideas: question.ideas?.map(idea => ({ ...idea, ...updates })) || []
                    };
                }
            });
            return {
                ...prev,
                guiding_questions: updatedGuidingQuestions,
                updatedAt: new Date().toISOString()
            };
        });
    }, []);

    const addIdeaToState = useCallback((questionId, ideaData) => {
        if (!currentBrainstorm) {
            throw new Error('Brainstorm not loaded');
        }
        
        const guidingQuestionsArr = Object.values(currentBrainstorm.guiding_questions);
        const question = guidingQuestionsArr.find(q => q.id === questionId);
        if (!question) {
            throw new Error('Guiding question not found');
        }
        
        const currentIdeasCount = (question.ideas || []).length;
        if (currentIdeasCount >= MAX_IDEAS_PER_QUESTION) {
            throw new Error('Maximum limit of 38 ideas per guiding question has been reached');
        }

        const newIdea = {
            id: uuidv4(),
            title: ideaData.title || '',
            description: ideaData.description || '',
            createdAt: new Date().toISOString(),
            ...ideaData
        };

        setCurrentBrainstorm(prev => {
            if (!prev) return null;
            const updatedGuidingQuestions = { ...prev.guiding_questions };
            Object.keys(updatedGuidingQuestions).forEach(key => {
                if (updatedGuidingQuestions[key].id === questionId) {
                    const question = updatedGuidingQuestions[key];
                    updatedGuidingQuestions[key] = {
                        ...question,
                        ideas: [...(question.ideas || []), newIdea]
                    };
                }
            });
            return {
                ...prev,
                guiding_questions: updatedGuidingQuestions,
                updatedAt: new Date().toISOString()
            };
        });
        
        return newIdea;
    }, [currentBrainstorm]);

    const addMultipleIdeasToState = useCallback((questionId, ideasData) => {
        if (!currentBrainstorm) {
            throw new Error('Brainstorm not loaded');
        }
        
        const guidingQuestionsArr = Object.values(currentBrainstorm.guiding_questions);
        const question = guidingQuestionsArr.find(q => q.id === questionId);
        if (!question) {
            throw new Error('Guiding question not found');
        }
        
        const currentIdeasCount = (question.ideas || []).length;
        const remainingSlots = MAX_IDEAS_PER_QUESTION - currentIdeasCount;
        
        if (remainingSlots <= 0) {
            throw new Error('Maximum limit of 38 ideas per guiding question has been reached');
        }

        const ideasToAdd = ideasData.slice(0, remainingSlots);
        const newIdeas = ideasToAdd.map(ideaData => ({
            id: uuidv4(),
            title: ideaData.title || '',
            description: ideaData.description || '',
            createdAt: new Date().toISOString(),
            ...ideaData
        }));

        setCurrentBrainstorm(prev => {
            if (!prev) return null;
            const updatedGuidingQuestions = { ...prev.guiding_questions };
            Object.keys(updatedGuidingQuestions).forEach(key => {
                if (updatedGuidingQuestions[key].id === questionId) {
                    const question = updatedGuidingQuestions[key];
                    updatedGuidingQuestions[key] = {
                        ...question,
                        ideas: [...(question.ideas || []), ...newIdeas]
                    };
                }
            });
            return {
                ...prev,
                guiding_questions: updatedGuidingQuestions,
                updatedAt: new Date().toISOString()
            };
        });
        
        return newIdeas;
    }, [currentBrainstorm]);

    const deleteIdeaFromState = useCallback((questionId, ideaId) => {
        setCurrentBrainstorm(prev => {
            if (!prev) return null;
            const updatedGuidingQuestions = { ...prev.guiding_questions };
            Object.keys(updatedGuidingQuestions).forEach(key => {
                if (updatedGuidingQuestions[key].id === questionId) {
                    const question = updatedGuidingQuestions[key];
                    updatedGuidingQuestions[key] = {
                        ...question,
                        ideas: question.ideas?.filter(idea => idea.id !== ideaId) || []
                    };
                }
            });
            return {
                ...prev,
                guiding_questions: updatedGuidingQuestions,
                updatedAt: new Date().toISOString()
            };
        });
    }, []);

    const deleteMultipleIdeasFromState = useCallback((questionId, ideaIds) => {
        setCurrentBrainstorm(prev => {
            if (!prev) return null;
            const updatedGuidingQuestions = { ...prev.guiding_questions };
            Object.keys(updatedGuidingQuestions).forEach(key => {
                if (updatedGuidingQuestions[key].id === questionId) {
                    const question = updatedGuidingQuestions[key];
                    updatedGuidingQuestions[key] = {
                        ...question,
                        ideas: question.ideas?.filter(idea => !ideaIds.includes(idea.id)) || []
                    };
                }
            });
            return {
                ...prev,
                guiding_questions: updatedGuidingQuestions,
                updatedAt: new Date().toISOString()
            };
        });
    }, []);

    const toggleKnowledgeBaseInState = useCallback((questionId) => {
        setCurrentBrainstorm(prev => {
            if (!prev) return null;
            const updatedGuidingQuestions = { ...prev.guiding_questions };
            Object.keys(updatedGuidingQuestions).forEach(key => {
                if (updatedGuidingQuestions[key].id === questionId) {
                    const question = updatedGuidingQuestions[key];
                    updatedGuidingQuestions[key] = {
                        ...question,
                        in_knowledge_base: !question.in_knowledge_base
                    };
                }
            });
            return {
                ...prev,
                guiding_questions: updatedGuidingQuestions,
                updatedAt: new Date().toISOString()
            };
        });
    }, []);

    const handleAddNewIdea = () => {
        setShowNewIdeaModal(true);
    }

    const handleAddMoreIdeas = async () => {
        try {
            if (!selectedGuidingQuestion || !currentBrainstorm) {
                Swal.fire({
                    title: 'Nenhuma questão selecionada',
                    text: 'Selecione uma questão norteadora para gerar mais ideias.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }

            const guidingQuestionsArr = Object.values(currentBrainstorm.guiding_questions);
            const currentGuidingQuestion = guidingQuestionsArr.find(q => q.id === selectedGuidingQuestion);
            if (!currentGuidingQuestion) {
                throw new Error('Guiding question not found');
            }

            const challenge = currentBrainstorm.challenges.find(c => c.id === currentGuidingQuestion.challenge_id);
            const challengeText = challenge?.text || '';

            setIsGeneratingMoreIdeas(true);

            const apiResponse = await generateIdeasByGuidingQuestion(
                parseInt(projectId),
                currentGuidingQuestion.text,
                challengeText,
                6,
                ideas || [],
                KEYCLOACK_TOKEN,
            );

            const generatedIdeas = apiResponse?.response?.ideias || [];
            
            const formattedIdeas = generatedIdeas.map((idea, index) => ({
                title: idea.titulo_da_ideia || `Ideia ${index + 1}`,
                description: idea.descricao_da_ideia || '',
                source: 'api_generated',
                selected: false, // New ideas start unselected
                guidingQuestionId: currentGuidingQuestion.id
            }));

            const newIdeas = addMultipleIdeasToState(selectedGuidingQuestion, formattedIdeas);
            
            // Update local ideas state - add new ideas to current ideas
            setIdeas(prevIdeas => [...prevIdeas, ...newIdeas]);

            const successMessage = formattedIdeas.length < generatedIdeas.length
                ? `${formattedIdeas.length} novas ideias foram adicionadas (limite de 38 ideias por questão norteadora).`
                : `${formattedIdeas.length} novas ideias foram adicionadas.`;

            Swal.fire({
                title: 'Ideias geradas!',
                text: successMessage,
                timer: 1500,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });

        } catch (error) {
            console.error('Error generating more ideas:', error);
            const errorMessage = error.message?.includes('Maximum limit of 38 ideas')
                ? 'Limite máximo de 38 ideias por questão norteadora foi atingido.'
                : 'Ocorreu um erro ao gerar mais ideias. Tente novamente.';
            
            Swal.fire({
                title: 'Erro ao gerar ideias',
                text: errorMessage,
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setIsGeneratingMoreIdeas(false);
        }
    };

    const handleNewIdeaConfirm = (newIdea) => {
        try {
            if (!selectedGuidingQuestion || !currentBrainstorm) {
                throw new Error('No guiding question selected');
            }

            const currentGuidingQuestion = guidingQuestions.find(q => q.id === selectedGuidingQuestion);
            if (!currentGuidingQuestion) {
                throw new Error('Guiding question not found');
            }

            const createdIdea = addIdeaToState(selectedGuidingQuestion, {
                title: newIdea.title,
                description: newIdea.description,
                selected: false,
                source: 'user_created'
            });

            // Update local ideas state
            setIdeas(prevIdeas => [...prevIdeas, createdIdea]);

            Swal.fire({
                title: 'Ideia criada!\u2728',
                text: 'A ideia foi adicionada com sucesso.',
                timer: 1500,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } catch (error) {
            console.error('Error creating idea:', error);
            const errorMessage = error.message?.includes('Maximum limit of 38 ideas')
                ? 'Limite máximo de 38 ideias por questão norteadora foi atingido.'
                : 'Ocorreu um erro ao criar a ideia. Tente novamente.';
            
            Swal.fire({
                title: 'Erro ao criar ideia',
                text: errorMessage,
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setShowNewIdeaModal(false);
        }
    };

    const handleNewIdeaCancel = () => {
        setShowNewIdeaModal(false);
    };

    const handleDeleteSelectedIdeas = async () => {
        try {
            if (!selectedGuidingQuestion || !currentBrainstorm) {
                return;
            }

            const selectedIdeas = ideas.filter(idea => idea.selected);
            
            if (selectedIdeas.length === 0) {
                Swal.fire({
                    title: 'Nenhuma ideia selecionada',
                    text: 'Selecione pelo menos uma ideia para excluir.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }
            
            const result = await Swal.fire({
                title: 'Confirmar exclusão em massa',
                text: `Tem certeza que deseja excluir ${selectedIdeas.length} ideia(s) selecionada(s)?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ff6b6b',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Sim, excluir todas!',
                cancelButtonText: 'Cancelar',
                background: '#27272A',
                color: '#fff',
            });
            
            if (result.isConfirmed) {
                const selectedIdeaIds = selectedIdeas.map(idea => idea.id);
                
                deleteMultipleIdeasFromState(selectedGuidingQuestion, selectedIdeaIds);
                
                // Update local ideas state
                const updatedIdeas = ideas.filter(idea => !selectedIdeaIds.includes(idea.id));
                setIdeas(updatedIdeas);
                
                Swal.fire({
                    title: 'Ideias excluídas!',
                    text: `${selectedIdeas.length} ideia(s) foram excluídas com sucesso.`,
                    timer: 1500,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
            }
            
        } catch (error) {
            console.error('Error deleting selected ideas:', error);
            Swal.fire({
                title: 'Erro ao excluir ideias',
                text: 'Ocorreu um erro ao excluir as ideias selecionadas. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        }
    };

    const handleGuidingQuestionSelect = (index, question) => {
        if (!currentBrainstorm) return;
        
        const questionIdeas = getIdeasByGuidingQuestionId(question.id);
        setIdeas(questionIdeas);
        setSelectedGuidingQuestion(selectedGuidingQuestion === question.id ? null : question.id);
    };

    const handleIdeaSelect = (ideaId) => {
        if (!selectedGuidingQuestion || !currentBrainstorm) return;
        
        const idea = ideas.find(i => i.id === ideaId);
        if (idea) {
            updateIdeaInState(selectedGuidingQuestion, ideaId, { selected: !idea.selected });
            
            // Update local ideas state
            const updatedIdeas = ideas.map(i => 
                i.id === ideaId ? { ...i, selected: !i.selected } : i
            );
            setIdeas(updatedIdeas);
        }
    };

    const handleSelectAll = () => {
        if (!selectedGuidingQuestion || !currentBrainstorm) return;
        
        updateMultipleIdeasInState(selectedGuidingQuestion, { selected: true });
        
        // Update local ideas state
        const updatedIdeas = ideas.map(idea => ({ ...idea, selected: true }));
        setIdeas(updatedIdeas);
    };

    const handleDownloadSelected = async () => {
        if (!currentBrainstorm) {
            setIsDownloadDisabled(true);
            return;
        }

        setLoadingPDF(true);
        
        try {
            // Get the selected challenge
            const currentChallenge = currentBrainstorm.challenges.find(c => c.selected === true);
            if (!currentChallenge) {
                setIsDownloadDisabled(true);
                setLoadingPDF(false);
                return;
            }

            const guidingQuestionsArr = Object.values(currentBrainstorm.guiding_questions);
            const formattedGuidingQuestions = guidingQuestionsArr
                .filter(question => question.ideas?.some(idea => idea.selected === true))
                .map(q => ({
                    ...q,
                    ideas: q.ideas.filter(idea => idea.selected === true)
                }));

            const payload = {
                templateName: 'brainstorm',
                challenge: currentChallenge.text,
                blocks: formattedGuidingQuestions,
                fileName: 'ideias-brainstorm'
            };
            
            const pdfFromServer = await renderIdeasPDF(payload, KEYCLOACK_TOKEN);

            if (pdfFromServer) {
                const byteArray = new Uint8Array(Object.values(pdfFromServer));
                const blob = new Blob([byteArray], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${payload.fileName}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Error downloading PDF:', error);
            Swal.fire({
                title: 'Erro ao baixar PDF',
                text: 'Ocorreu um erro ao gerar o PDF. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setLoadingPDF(false);
        }
    };

    const handleCompleteSelection = () => {
        setShowKnowledgeBaseModal(true);
    };

    const handleToggleKnowledgeBase = (questionId) => {
        toggleKnowledgeBaseInState(questionId);
    };

    const generateNewFileName = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();

        return 'brainstorm_' + year + month + day + '_' + hours + minutes + seconds;
    }

    const handleFinishKnowledgeBaseReview = async (updatedBrainstormData) => {
        try {
            if (!updatedBrainstormData) {
                Swal.fire({
                    icon: 'error',
                    title: 'Erro ao finalizar trabalho',
                    text: 'Erro ao buscar dados atualizados. Tente novamente.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }

            setLoadingKnowledgeBase(true);
            
            const guidingQuestionsArr = Object.values(updatedBrainstormData.guiding_questions);
            const allBrainstormIdeas = guidingQuestionsArr.map(question => question.ideas);
            const payload = {
                projectId: projectId,
                data: {
                    challenges: updatedBrainstormData.challenges,
                    guidingQuestions: {...updatedBrainstormData.guiding_questions},
                    ideas: {...allBrainstormIdeas.flat()},
                    groups: {...updatedBrainstormData.groups},
                    concepts: {...updatedBrainstormData.concepts}
                }
            };

            const BrainstormDataAfterUpdate = await updateBrainstorm(outputId, payload, KEYCLOACK_TOKEN);
            
            if (BrainstormDataAfterUpdate) {
                const currentChallenge = BrainstormDataAfterUpdate.data.data.challenges.find(c => c.selected === true);
                if (!currentChallenge) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro ao finalizar trabalho',
                        text: 'Erro ao identificar desafio principal. Tente novamente.',
                    });
                    return;
                }

                const formattedGuidingQuestions = guidingQuestionsArr
                    .filter(question => question.in_knowledge_base && question.ideas?.some(idea => idea.selected))
                    .map(q => ({
                        ...q,
                        ideas: q.ideas.filter(idea => idea.selected)
                    }));

                const newFileName = generateNewFileName();

                const assetPayload = {
                    templateName: 'brainstorm',
                    challenge: currentChallenge.text,
                    blocks: formattedGuidingQuestions,
                    fileName: newFileName
                };

                const assetKnowledgeBase = await createAssetKnowledgeBase(projectId, assetPayload, newFileName, KEYCLOACK_TOKEN);
                if (assetKnowledgeBase) {
                    Swal.fire({
                        title: 'Trabalho atualizado!',
                        text: 'O trabalho foi atualizado com sucesso.',
                        timer: 1500,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
        
                    navigate(`/project/${projectId}`);
                } else {
                    Swal.fire({
                        title: 'Erro ao criar asset da base de conhecimento',
                        text: 'Ocorreu um erro ao criar o asset da base de conhecimento. Tente novamente.',
                        timer: 2000,
                        showConfirmButton: false,
                        background: '#27272A',
                        color: '#fff',
                        position: 'top-end',
                    });
                    return;
                }
            } else {
                Swal.fire({
                    title: 'Erro ao atualizar trabalho',
                    text: 'Ocorreu um erro ao editar o trabalho. Tente novamente.',
                    timer: 2000,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                return;
            }

        } catch (error) {
            console.error('Error finishing knowledge base review:', error);
            Swal.fire({
                title: 'Erro ao finalizar trabalho',
                text: 'Ocorreu um erro ao concluir a revisão da base de conhecimento. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setShowKnowledgeBaseModal(false);
            setLoadingKnowledgeBase(false);
        }
    };

    const handleEditIdea = (ideaId) => {
        const idea = ideas.find(i => i.id === ideaId);
        if (idea) {
            setEditingIdea(idea);
            setShowEditModal(true);
        }
    };
    
    const handleEditConfirm = (editedIdea) => {
        try {
            if (!selectedGuidingQuestion || !currentBrainstorm) {
                throw new Error('No guiding question selected');
            }

            const guidingQuestionsArr = Object.values(currentBrainstorm.guiding_questions);
            const currentGuidingQuestion = guidingQuestionsArr.find(q => q.id === selectedGuidingQuestion);
            
            if (!currentGuidingQuestion) {
                throw new Error('Guiding question not found');
            }
            
            updateIdeaInState(
                selectedGuidingQuestion,
                editedIdea.id,
                {
                    title: editedIdea.title,
                    description: editedIdea.description
                }
            );
            
            const updatedIdeas = ideas.map(idea =>
                idea.id === editedIdea.id
                    ? { ...idea, title: editedIdea.title, description: editedIdea.description }
                    : idea
            );
            setIdeas(updatedIdeas);

            Swal.fire({
                title: 'Ideia atualizada!',
                text: 'A ideia foi atualizada com sucesso.',
                timer: 1500,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
            
        } catch (error) {
            console.error('Error updating idea:', error);
            Swal.fire({
                title: 'Erro ao atualizar ideia',
                text: 'Ocorreu um erro ao atualizar a ideia. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        } finally {
            setShowEditModal(false);
            setEditingIdea(null);
        }
    };
    
    const handleEditCancel = () => {
        setShowEditModal(false);
        setEditingIdea(null);
    };
    
    const handleDeleteIdea = async (ideaId) => {
        try {
            if (!selectedGuidingQuestion || !currentBrainstorm) {
                return;
            }

            const idea = ideas.find(i => i.id === ideaId);
            const ideaTitle = idea?.title || 'esta ideia';
            
            const result = await Swal.fire({
                title: 'Confirmar exclusão',
                text: `Tem certeza que deseja excluir "${ideaTitle}"?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ff6b6b',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Sim, excluir!',
                cancelButtonText: 'Cancelar',
                background: '#27272A',
                color: '#fff',
            });
            
            if (result.isConfirmed) {
                deleteIdeaFromState(selectedGuidingQuestion, ideaId);
                
                const updatedIdeas = ideas.filter(idea => idea.id !== ideaId);
                setIdeas(updatedIdeas);
                
                Swal.fire({
                    title: 'Ideia excluída!',
                    text: 'A ideia foi excluída com sucesso.',
                    timer: 1500,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
            }
            
        } catch (error) {
            console.error('Error deleting idea:', error);
            Swal.fire({
                title: 'Erro ao excluir ideia',
                text: 'Ocorreu um erro ao excluir a ideia. Tente novamente.',
                timer: 2000,
                showConfirmButton: false,
                background: '#27272A',
                color: '#fff',
                position: 'top-end',
            });
        }
    };

    return (
        <>
            <EditIdeaModal
                show={showEditModal}
                onHide={handleEditCancel}
                idea={editingIdea}
                onConfirm={handleEditConfirm}
            />

            <NewIdeaModal
                show={showNewIdeaModal}
                onHide={handleNewIdeaCancel}
                onConfirm={handleNewIdeaConfirm}
            />

            <KnowledgeBaseModal
                show={showKnowledgeBaseModal}
                onHide={() => setShowKnowledgeBaseModal(false)}
                outputId={outputId}
                onToggleKnowledgeBase={handleToggleKnowledgeBase}
                handleConclude={handleFinishKnowledgeBaseReview}
                loading={loadingKnowledgeBase}
                brainstormData={currentBrainstorm}
            />

            <Container>
            <section id="brainstorm-ideas-header" className="mb-3">
                    <div className="d-flex flex-row align-items-center justify-content-between">
                        <h3>Novas ideias geradas</h3>
                        
                        <div className="d-flex flex-row gap-2">
                            <ButtonSecondary
                                icon={loadingPDF ? <Loader size={20} /> : <FaDownload />}
                                onClick={handleDownloadSelected}
                                disabled={loadingPDF || isDownloadDisabled}
                            >
                                {loadingPDF ? "Preparando arquivo..." : 'Baixar selecionadas'}
                            </ButtonSecondary>
                            
                            <ButtonMain onClick={handleCompleteSelection} disabled={isLoadingData}>
                                Concluir edição
                            </ButtonMain>
                        </div>
                    </div>
                </section>
            </Container>

            <div className="brainstorm-ideas-header" />

            <section id="brainstorm-ideas-content">
                <Container>
                    <div className="row brainstorm-ideas-layout">
                        {/* Left Column - Problem Statements */}
                        <div className="col-12 col-lg-4 brainstorm-ideas-left-column">
                            <div className="brainstorm-ideas-section-header">
                                <p>Como podemos...</p>
                            </div>
                            
                            <div className="brainstorm-ideas-scrollable-content">
                                {guidingQuestions.map((question, index) => (
                                    <div 
                                        key={index} 
                                        className={`problem-statement-item ${selectedGuidingQuestion === question.id ? 'selected' : ''}`}
                                        onClick={() => handleGuidingQuestionSelect(index, question)}
                                        role="button"
                                        tabIndex={0}
                                        aria-pressed={selectedGuidingQuestion === question.id}
                                    >
                                        <p>{question.text}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right Column - Idea Cards */}
                        <div className="col-12 col-lg-8 brainstorm-ideas-right-column">
                            <div className="brainstorm-ideas-section-header">
                                <p>Selecione as ideias que quer adicionar a base de conhecimento ou baixar</p>
                                
                                <div className="brainstorm-ideas-actions">
                                    <button 
                                        className="btn"
                                        onClick={handleAddNewIdea}
                                        title="Adicionar nova ideia"
                                    >
                                        <FaPlus size={14} />
                                    </button>
                                    <button 
                                        className="btn"
                                        onClick={handleSelectAll}
                                        title="Selecionar todas"
                                    >
                                        <IoMdCheckboxOutline size={17} />
                                    </button>
                                    <button 
                                        className="btn"
                                        onClick={() => {
                                            if (!selectedGuidingQuestion || !currentBrainstorm) return;
                                            updateMultipleIdeasInState(selectedGuidingQuestion, { selected: false });
                                            const updatedIdeas = ideas.map(idea => ({ ...idea, selected: false }));
                                            setIdeas(updatedIdeas);
                                        }}
                                        title="Desmarcar todas"
                                    >
                                        <CgCloseR size={14} />
                                    </button>
                                    <button 
                                            className="btn"
                                        onClick={handleDeleteSelectedIdeas}
                                        title="Excluir selecionadas"
                                    >
                                        <FaRegTrashAlt size={14} />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="brainstorm-ideas-scrollable-content">
                                <div className="ideas-grid">
                                    {isLoadingData ? (
                                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                                            <div className="d-flex flex-column align-items-center justify-content-center">
                                                <AnimatedBrainLoader size={200} />
                                                <p className="text-secondary">Carregando dados...</p>                                                
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {ideas.map((idea) => (
                                                <IdeaCard
                                                    key={idea.id}
                                                    id={idea.id}
                                                    title={idea.title}
                                                    description={idea.description}
                                                    isSelected={idea.selected}
                                                    onSelect={handleIdeaSelect}
                                                    onEdit={handleEditIdea}
                                                    onEditConfirm={handleEditConfirm}
                                                    onDelete={handleDeleteIdea}
                                                />
                                            ))}

                                            <button 
                                                className="generate-more-ideas-button" 
                                                onClick={handleAddMoreIdeas}
                                                disabled={isGeneratingMoreIdeas}
                                            >
                                                <div
                                                    className="generate-more-ideas"
                                                    style={{ cursor: isGeneratingMoreIdeas ? 'not-allowed' : 'pointer' }}
                                                >
                                                    <div className="generate-more-content">
                                                        {isGeneratingMoreIdeas ? (
                                                            <p>
                                                                <Loader size={20} />
                                                            </p>
                                                        ) : (
                                                            <p><LuSparkles size={20} /> Gerar + 6</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
        </>
    );
}